const MAX_MB = 10;

const TIPOS_PERMITIDOS = ["png", "jpg", "webp", "avif"] as const;
type TipoImagen = (typeof TIPOS_PERMITIDOS)[number];

const ETIQUETAS: Record<TipoImagen, string> = {
    png: "PNG",
    jpg: "JPG",
    webp: "WebP",
    avif: "AVIF",
};

const leerCabecera = (file: File): Promise<Uint8Array> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(file.slice(0, 16));
    });

const empiezaCon = (bytes: Uint8Array, firma: number[]) =>
    firma.every((b, i) => bytes[i] === b);

const texto = (bytes: Uint8Array, desde: number, hasta: number) => {
    let s = "";
    for (let i = desde; i < hasta; i++) {
        s += String.fromCharCode(bytes[i]);
    }
    return s;
};

/** Identifica el formato real leyendo la firma del archivo. */
export const detectarTipoImagen = (bytes: Uint8Array): TipoImagen | "heic" | null => {
    if (empiezaCon(bytes, [0x89, 0x50, 0x4e, 0x47])) return "png";
    if (empiezaCon(bytes, [0xff, 0xd8, 0xff])) return "jpg";

    // RIFF....WEBP
    if (texto(bytes, 0, 4) === "RIFF" && texto(bytes, 8, 12) === "WEBP") return "webp";

    // Contenedor ISOBMFF: "ftyp" en el offset 4, marca en el 8
    if (texto(bytes, 4, 8) === "ftyp") {
        const marca = texto(bytes, 8, 12);
        if (marca === "avif" || marca === "avis") return "avif";
        if (["heic", "heix", "hevc", "mif1", "msf1"].includes(marca)) return "heic";
    }

    return null;
};

export interface ResultadoValidacionImagen {
    error: string | null;
    /** Formato real detectado, útil para nombrar el archivo correctamente. */
    tipo: TipoImagen | null;
}

export const validarImagenReferencia = async (
    file: File
): Promise<ResultadoValidacionImagen> => {
    if (file.size === 0) {
        return { error: "El archivo está vacío.", tipo: null };
    }
    if (file.size > MAX_MB * 1024 * 1024) {
        const mb = (file.size / 1024 / 1024).toFixed(1);
        return {
            error: `La imagen pesa ${mb} MB y el máximo permitido es ${MAX_MB} MB.`,
            tipo: null,
        };
    }

    const tipo = detectarTipoImagen(await leerCabecera(file));

    if (tipo === "heic") {
        return {
            error: "Las imágenes HEIC no son compatibles. Guárdala como PNG, JPG, WebP o AVIF.",
            tipo: null,
        };
    }
    if (!tipo) {
        return {
            error: "El archivo no es una imagen válida. Debe ser PNG, JPG, WebP o AVIF.",
            tipo: null,
        };
    }

    return { error: null, tipo };
};

export const etiquetaTipoImagen = (tipo: TipoImagen) => ETIQUETAS[tipo];